<template>
  <div class="admin-duplicates">
    <div class="admin-duplicates-header">
      <h3>Outil admin – Doublons potentiels</h3>
      <button :disabled="detecting" @click="detect">
        {{ detecting ? 'Analyse en cours...' : 'Analyser les doublons' }}
      </button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <template v-if="groups !== null">
      <p v-if="!groups.length" class="no-duplicates">✅ Aucun doublon potentiel détecté.</p>
      <template v-else>
        <p class="duplicates-count">{{ groups.length }} groupe(s) de doublons détecté(s)</p>
        <div v-for="(group, groupIndex) in groups" class="duplicate-group" :key="group.map((i) => i.id).join('-')">
          <div class="duplicate-cards">
            <div v-for="ins in group" class="duplicate-card" :class="{ 'is-selected': selection[groupIndex] === ins.id }"
              :key="ins.id">
              <div class="dup-name">{{ ins.name.toUpperCase() }} {{ ins.firstname }}</div>
              <div class="dup-info">{{ ins.club || '-' }}</div>
              <div class="dup-info">Ind : {{ ins.category_simple || '-' }} | Dbl : {{ ins.category_double || '-' }}</div>
              <button @click="selection[groupIndex] = ins.id">
                {{ selection[groupIndex] === ins.id ? '✓ À conserver' : 'Conserver celui-ci' }}
              </button>
            </div>
          </div>
          <div class="merge-action">
            <button @click="dismiss(groupIndex)">Conserver les 2</button>
            <button v-if="selection[groupIndex]" class="merge-button" :disabled="merging[groupIndex]"
              @click="merge(groupIndex)">
              {{ merging[groupIndex] ? 'Fusion...' : 'Fusionner (supprimer les autres)' }}
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import backendApi from '@/axios/backendApi';

// Détection et fusion des inscriptions en double d'un événement (même joueur inscrit deux
// fois, orthographe proche), repris de l'ancienne liste des inscrits de CMER. Réservé aux
// admins (cf. EventRoster.vue) ; le backend vérifie en plus le droit sur l'événement
// (inscriptions/routes.py::detect_duplicates, merge_inscriptions).
const props = defineProps<{ eventId: number }>();
const emit = defineEmits<{ merged: [] }>();

interface DuplicateInscription {
  id: number;
  name: string;
  firstname: string;
  club: string | null;
  category_simple: string | null;
  category_double: string | null;
}

const groups = ref<DuplicateInscription[][] | null>(null);
const detecting = ref(false);
const selection = ref<Record<number, number>>({});
const merging = ref<Record<number, boolean>>({});
const error = ref('');

const detect = async () => {
  detecting.value = true;
  groups.value = null;
  selection.value = {};
  error.value = '';
  try {
    const { data } = await backendApi.get('/inscriptions/detect-duplicates', { params: { event_id: props.eventId } });
    groups.value = data.duplicate_groups;
  } catch {
    error.value = 'Erreur lors de la détection des doublons.';
  } finally {
    detecting.value = false;
  }
};

const removeGroup = (groupIndex: number) => {
  groups.value!.splice(groupIndex, 1);
  selection.value = {};
};

const dismiss = removeGroup;

const merge = async (groupIndex: number) => {
  const keepId = selection.value[groupIndex];
  const toDelete = groups.value![groupIndex].filter((ins) => ins.id !== keepId);
  merging.value[groupIndex] = true;
  error.value = '';
  try {
    for (const del of toDelete) {
      await backendApi.post('/inscriptions/merge', { keep_id: keepId, delete_id: del.id });
    }
    removeGroup(groupIndex);
    emit('merged');
  } catch {
    error.value = 'Erreur lors de la fusion.';
  } finally {
    merging.value[groupIndex] = false;
  }
};
</script>

<style scoped>
.admin-duplicates {
  margin-top: 2rem;
  padding: 1rem 1.25rem;
  border: 1px dashed var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
}

.admin-duplicates-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.admin-duplicates-header h3 {
  margin: 0;
  font-size: 1rem;
}

.duplicate-group {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-light-shadow, #ddd);
}

.duplicate-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.duplicate-card {
  flex: 1 1 220px;
  padding: 0.75rem;
  border: 1px solid var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
  background: var(--color-bg-lighter, #f5f5f5);
}

.duplicate-card.is-selected {
  border-color: var(--color-success, #27ae60);
}

.dup-name {
  font-weight: 600;
}

.dup-info {
  font-size: 0.85rem;
  margin: 0.2rem 0;
}

.merge-button {
  background: var(--color-error, #dc2626);
  color: #fff;
}

.merge-action {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
</style>
